import React, { useEffect } from 'react';
import { useDispatch, connect } from 'react-redux';

import PropTypes from 'prop-types';
import { compose } from 'redux';

// components
import Spinner from 'now-frontend-shared/components/Spinner';

// layouts
import HeadSection from 'now-frontend-shared/layouts/AuthSections/HeadSection';

// helpers
import { DEFAULT_QUERY } from 'now-frontend-shared/utils/helpers';

// styles and components from material-ui
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import { DataGrid } from '@mui/x-data-grid';

// styles
import { getPropertyBids, clearState } from 'store/actions/propertyBidsListActions';
import styles from './styles';

import { SortDirection } from 'now-shared/enums/sort-direction';
import { getCompanyName } from 'now-shared/helpers/company-helpers';
import {
  clearListingAnalyticsState,
  getListingAnalytics,
} from 'store/actions/listingAnalyticsActions';
import { pluralize } from 'now-shared/helpers/text-helpers';
import { HighLevelStatCard, minSpaceBetweenStatCardsHalf } from 'now-frontend-shared/components/StatCard';

const statCardRowMarginSides = 32;

const ListingAnalyticsPage = ({
  classes,
  match,
  commonInfo,
  propertyInfoIsLoading,
  analytics,
  analyticsIsLoading,
}) => {
  const dispatch = useDispatch();

  const listingId = match.params.id;
  // TODO: [PERFORMANCE] fetch the base listing info without querying for bids
  const query = `${DEFAULT_QUERY}&order=amount&sort=${SortDirection.Descending}`;

  useEffect(() => {
    dispatch(getListingAnalytics({
      listingId,
    }));

    return () => dispatch(clearListingAnalyticsState());
  }, [dispatch, listingId]);

  useEffect(() => {
    dispatch(getPropertyBids({
      id: listingId,
      query,
    }));

    return () => dispatch(clearState());
  }, [dispatch, listingId, query]);

  if (!commonInfo || !analytics) {
    return <Spinner wrapped />;
  }

  return (
    <>
      <Grid container className={classes.relativeContainer}>
        {(analyticsIsLoading || propertyInfoIsLoading) && <Spinner backdrop />}
        <Grid container direction="column" className={classes.wrapper}>
          <HeadSection
            heading={(
              <>
                <span>
                  Reports & Analytics Details
                  <br />
                </span>
                <span>
                  {`Listing-${commonInfo.id} | ${commonInfo.projectName} (${commonInfo.wells.length} well${pluralize('s', commonInfo.wells.length)})`}
                </span>
              </>
            )}
            ignorePrevPath
            path={`/listings/${listingId}`}
          />
          <Grid
            item
            xs={12}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              style={{
                paddingLeft: statCardRowMarginSides - minSpaceBetweenStatCardsHalf,
                paddingRight: statCardRowMarginSides - minSpaceBetweenStatCardsHalf,
                paddingTop: statCardRowMarginSides,
                paddingBottom: statCardRowMarginSides,
              }}
            >
              <HighLevelStatCard
                title="Total Views"
                value={analytics.totalViews}
              />
              <HighLevelStatCard
                title="Total Downloads"
                value={analytics.totalDownloads}
              />
              <HighLevelStatCard
                title="Total Bids"
                value={analytics.totalBids}
              />
            </Box>
          </Grid>
          <Grid container direction="row">
            {!analytics.companies.length ? (
              <Grid container justify="center">
                <span className={classes.message}>No activity</span>
              </Grid>
            ) : (
              <>
                <DataGrid
                  rows={analytics.companies}
                  autoHeight
                  autoPageSize
                  disableColumnMenu
                  disableColumnSelector
                  disableSelectionOnClick
                  columns={[
                    {
                      field: 'companyName',
                      headerName: 'COMPANY',
                      flex: 1,
                      valueGetter: ({ row }) => getCompanyName(row),
                    },
                    {
                      field: 'views',
                      headerName: 'VIEWS',
                      flex: 1,
                    },
                    {
                      field: 'downloads',
                      headerName: 'DOWNLOADS',
                      flex: 1,
                    },
                    {
                      field: 'hasBid',
                      headerName: 'BIDS',
                      flex: 1,
                      valueFormatter: ({ value }) => (value ? 'Yes' : 'No'),
                    },
                  ]}
                />
              </>
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

ListingAnalyticsPage.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  analytics: PropTypes.shape({
    companies: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      fullLegalCompanyName: PropTypes.string.isRequired,
      operatingName: PropTypes.string,
      views: PropTypes.number.isRequired,
      downloads: PropTypes.number.isRequired,
      hasBid: PropTypes.bool.isRequired,
    })).isRequired,
    totalViews: PropTypes.number.isRequired,
    totalDownloads: PropTypes.number.isRequired,
    totalBids: PropTypes.number.isRequired,
  }),
  analyticsIsLoading: PropTypes.bool.isRequired,
  commonInfo: PropTypes.shape({
    // TODO: [TYPE] add specific field types
  }),
  propertyInfoIsLoading: PropTypes.bool.isRequired,
};

ListingAnalyticsPage.defaultProps = {
  analytics: undefined,
  commonInfo: undefined,
};

export default compose(
  connect(({ propertyBidsList, listingAnalytics }) => ({
    commonInfo: propertyBidsList.commonInfo,
    propertyInfoIsLoading: propertyBidsList.pending,
    analytics: listingAnalytics.analytics,
    analyticsIsLoading: listingAnalytics.analyticsIsLoading,
  })),
  withStyles(styles),
)(ListingAnalyticsPage);
